import { AlertService } from '@anecdotes/design-system/alert';
import {
  RadioButtonsGroupControl,
  RichTextareaControl,
  TextFieldControl,
  ToggleBoxControl,
} from '@anecdotes/design-system/form-controls';
import { ModalWindowService } from '@anecdotes/design-system/modals';
import { TipTypeEnum } from '@anecdotes/design-system/tips';

import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnInit,
  TemplateRef,
  ViewChild, inject, DestroyRef } from '@angular/core';
import { Validators } from '@angular/forms';
import { DynamicFormGroup, TemplateModel } from '@anecdotes/dynamic-form';
import { Observable, Subject, take } from 'rxjs';
import { EvidenceTargetResource } from '@anecdotes/automation';
import { RegularDateFormatMMMdyyyy } from '@anecdotes/helpers';
import {
  TrustCenterFacadeService,
  Draft,
  PolicyResponse,
  TrustCenterResourceTypes,
  EvidenceFile,
} from '@anecdotes/shared-trust-center';
import { PolicyNavigationService } from '@anecdotes/shared-policy';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export enum FormControls {
  Name = 'name',
  Description = 'description',
  IsVisible = 'isVisible',
  FileDisplayName = 'fileDisplayName',
  IsPublic = 'isPublic',
}

const enum FileAccess {
  Private = 'private',
  Public = 'public',
}

interface InitialValues {
  [FormControls.Name]: string;
  [FormControls.IsVisible]: boolean;
  [FormControls.Description]: string;
  [FormControls.FileDisplayName]: string;
  [FormControls.IsPublic]: string;
}

@Component({
  selector: 'app-policy-edit-side-panel',
  templateUrl: './policy-edit-side-panel.component.html',
  styleUrls: ['./policy-edit-side-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PolicyEditSidePanelComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  @HostBinding('class')
  private readonly classes = 'h-full';

  private initialFormValues: InitialValues;

  readonly tipTypes = TipTypeEnum;

  readonly sendingRequestBtnLoader$ = new Subject<boolean>();

  readonly dateFormat = RegularDateFormatMMMdyyyy;

  @ViewChild('middleFormTmpl', { static: true })
  private middleFormTmpl: TemplateRef<any>;

  @Input()
  policy: PolicyResponse;

  form: DynamicFormGroup<any>;

  draft$: Observable<Draft>;

  targetResource: EvidenceTargetResource;

  get wasFormValueChanged(): boolean {
    if (!this.form) {
      return false;
    }

    return Object.values(FormControls).some((name) => this.form.items[name].value !== this.initialFormValues[name]);
  }

  get isFormValid(): boolean {
    if (!this.form) {
      return false;
    }

    return this.form.valid && this.wasFormValueChanged;
  }

  get shouldShowFileAccessWarning(): boolean {
    return this.evidence.evidence_id && this.form.controls[FormControls.IsPublic].value === FileAccess.Public;
  }

  get evidence(): EvidenceFile {
    return this.policy.evidence_files[0];
  }

  constructor(
    private trustCenterFacadeService: TrustCenterFacadeService,
    private modalWindowService: ModalWindowService,
    private alertService: AlertService,
    private policyNavigationService: PolicyNavigationService,
  ) {}

  ngOnInit(): void {
    this.draft$ = this.trustCenterFacadeService.getDraft(TrustCenterResourceTypes.Policy, this.policy.id);
    this.trustCenterFacadeService.initDraft(TrustCenterResourceTypes.Policy, this.policy.id);

    this.initForm();

    this.targetResource = {
      resourceId: this.policy.id,
      resourceType: TrustCenterResourceTypes.Policy,
    };
  }

  buildTranslationKey(relativeKey: string): string {
    return `trustCenter.${relativeKey}`;
  }

  async saveBtnClick(): Promise<void> {
    if (!this.wasFormValueChanged) {
      return;
    }

    this.sendingRequestBtnLoader$.next(true);

    try {
      await this.trustCenterFacadeService.updatePolicy(this.policy.id, {
        name: this.form.controls[FormControls.Name].value,
        description: this.form.controls[FormControls.Description].value || '',
        is_visible: this.form.controls[FormControls.IsVisible].value,
        evidence_files: [
          {
            evidence_id: this.evidence.evidence_id,
            filename: this.evidence.filename,
            trustcenter_display_name: this.form.controls[FormControls.FileDisplayName].value,
            is_public: this.form.controls[FormControls.IsPublic].value === FileAccess.Public,
          }
        ],
      });
      this.closePanel();
    } catch {
      this.alertService.openActionErrorAlert({
        customBaseTranslationKey: this.buildTranslationKey('tryAgainError'),
      });
    } finally {
      this.sendingRequestBtnLoader$.next(false);
    }
  }

  closePanel(): void {
    this.trustCenterFacadeService.clearDraft(TrustCenterResourceTypes.Policy, this.policy.id);
    this.modalWindowService.close();
  }

  modalOpened(): void {
    this.trustCenterFacadeService.updateDraft({
      draftType: TrustCenterResourceTypes.Policy,
      id: this.policy.id,
      formData: this.form.value,
    });
  }

  resetPolicyName(): void {
    this.form.controls[FormControls.Name].setValue(this.policy.original_name);
  }

  navigateToPolicy(): void {
    this.policyNavigationService.navigateToPolicyById(this.policy.id);
  }

  private initForm(): void {
    this.draft$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((draft) => {
      const formDraft = draft?.formData;

      this.initialFormValues = {
        [FormControls.Name]: this.policy.name,
        [FormControls.Description]: this.policy.description,
        [FormControls.IsVisible]: this.policy.is_visible,
        [FormControls.IsPublic]: this.evidence.is_public ? FileAccess.Public : FileAccess.Private,
        [FormControls.FileDisplayName]: this.evidence.trustcenter_display_name,
      };

      const initialValuesWithDraft: InitialValues = formDraft || this.initialFormValues;

      this.form = new DynamicFormGroup({
        [FormControls.IsVisible]: new ToggleBoxControl({
          initialInputs: {
            title: 'Show',
          },
          initialValue: initialValuesWithDraft[FormControls.IsVisible],
        }),
        [FormControls.Name]: new TextFieldControl({
          initialInputs: {
            validateOnDirty: true,
            required: true,
            label: this.buildTranslationKey('policy.nameFieldName'),
          },
          initialValue: initialValuesWithDraft[FormControls.Name],
          validators: [Validators.required],
        }),
        [FormControls.Description]: new RichTextareaControl({
          initialInputs: {
            validateOnDirty: true,
            label: this.buildTranslationKey('policy.descriptionFieldName'),
            placeholder: '',
            minTextareaHeight: 154,
          },
          initialValue: initialValuesWithDraft[FormControls.Description],
        }),
        middleFormTmpl: new TemplateModel(undefined, this.middleFormTmpl),
        [FormControls.FileDisplayName]: new TextFieldControl({
          initialInputs: {
            validateOnDirty: true,
            required: true,
            label: this.buildTranslationKey('policy.fileDisplayName'),
            placeholder: this.buildTranslationKey('policy.fileName'),
          },
          initialValue: initialValuesWithDraft[FormControls.FileDisplayName],
          validators: [Validators.required],
        }),
        [FormControls.IsPublic]: new RadioButtonsGroupControl({
          initialInputs: {
            required: true,
            label: this.buildTranslationKey('policy.isPublic'),
            sublabel: this.buildTranslationKey('policy.isPublicDescription'),
            buttons: [
              {
                value: FileAccess.Private,
                label: this.buildTranslationKey('evidenceSummary.private'),
                icon: 'lock',
              },
              {
                value: FileAccess.Public,
                label: this.buildTranslationKey('evidenceSummary.public'),
              },
            ],
          },
          initialValue: initialValuesWithDraft.isPublic,
        }),
      });
    });
  }
}
